import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AuthImg } from "@/components/AuthImg";
import { Loader2, Check, Undo2 } from "lucide-react";

const LABEL = {
  punctuation: "spacing or punctuation only",
  wording: "assembly / plural wording",
  synonym: "two words for the same thing",
  abbreviation: "a short form of the same name",
  contains: "one name plus extra words",
  near_miss: "the crew hit this pair while naming",
};

export const ReasonChip = ({ reason }) => (
  <Badge data-testid={`naming-reason-${reason}`} className="rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-semibold">
    {LABEL[reason] || reason}
  </Badge>
);

export const NameThumb = ({ fileId, alt }) => (
  fileId
    ? <AuthImg fileId={fileId} thumb alt={alt} className="w-10 h-10 rounded-md object-cover border border-slate-200 bg-slate-50" />
    : <div className="w-10 h-10 rounded-md border border-dashed border-slate-200 bg-slate-50" />
);

/** THE LIST OF WHAT IT WILL HIT — on screen before anything applies, NOTHING ticked. */
export function PartTickList({ lookup, set, queue, archiveNames, onDone, testid }) {
  const [parts, setParts] = useState(null);
  const [extent, setExtent] = useState({ total: 0, has_more: false });
  const [ticked, setTicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setParts(null); setTicked(new Set());
    api.post("/naming/parts?page_size=200", lookup)
      .then(({ data }) => { if (!live) return; setParts(data.items || []); setExtent(data); })
      .catch((e) => { if (live) { setParts([]); toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load those parts."); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(lookup)]);

  const toggle = (id) => setTicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const eligible = useMemo(() => (parts || []).filter((p) => {
    if (set.name) return (p.name || "").trim().toLowerCase() !== set.name.trim().toLowerCase();
    if (set.part_type_id) return String(p.part_type_id || "") !== String(set.part_type_id);
    if (set.category_id) return String(p.category_id || "") !== String(set.category_id);
    return true;
  }), [parts, set]);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/naming/apply", {
        part_ids: [...ticked], set, queue, archive_names: archiveNames || [],
      });
      const kept = (data.kept_names || []).map((k) => `"${k.name}" stays in the list — ${k.parts_left} part${k.parts_left === 1 ? "" : "s"} still use it`);
      toast.success(`${data.changed} part${data.changed === 1 ? "" : "s"} updated${data.archived_names?.length ? ` · ${data.archived_names.length} name${data.archived_names.length === 1 ? "" : "s"} archived` : ""}`);
      if (kept.length) toast.info(kept.join(" · "), { duration: 8000 });
      if (data.skipped?.length) toast.info(`${data.skipped.length} skipped — ${data.skipped[0].why}`);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That did not apply.");
    } finally { setBusy(false); }
  };

  if (parts === null) return <div className="font-mono text-xs text-slate-400 py-4">LOADING THE PARTS…</div>;

  return (
    <div data-testid={testid} className="mt-3 border-t border-slate-200 pt-3">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-xs text-slate-500">
          {eligible.length} part{eligible.length === 1 ? "" : "s"} would change · nothing is ticked
          {extent.has_more ? ` · showing ${parts.length} of ${extent.total}` : ""}
        </span>
        <Button data-testid={`${testid}-all`} size="sm" variant="outline" className="rounded-lg h-9 sm:h-7 text-xs"
          onClick={() => setTicked(new Set(eligible.map((p) => p.id)))}>Tick all {eligible.length}</Button>
        {ticked.size > 0 && (
          <Button data-testid={`${testid}-none`} size="sm" variant="outline" className="rounded-lg h-9 sm:h-7 text-xs"
            onClick={() => setTicked(new Set())}>Clear</Button>
        )}
      </div>
      <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
        {eligible.length === 0 && <div className="text-sm text-slate-400">Every part here already has it.</div>}
        {eligible.map((p) => (
          <label key={p.id} data-testid={`${testid}-row-${p.id}`}
            className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 cursor-pointer hover:border-slate-300">
            <Checkbox data-testid={`${testid}-tick-${p.id}`} checked={ticked.has(p.id)} onCheckedChange={() => toggle(p.id)} />
            <NameThumb fileId={p.photo_file_id} alt={p.name} />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-slate-800 truncate">{p.name || "— no name —"}</span>
              <span className="block text-[11px] text-slate-400 font-mono">
                {p.short_code}{p.part_type_name ? ` · ${p.part_type_name}` : " · no part type"}{p.truck ? ` · ${p.truck}` : ""}
              </span>
            </span>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button data-testid={`${testid}-apply`} disabled={busy || ticked.size === 0} onClick={apply}
          className="rounded-lg h-9 bg-[#B01E28] hover:bg-[#8e1820] text-white">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Apply to {ticked.size} part{ticked.size === 1 ? "" : "s"}</>}
        </Button>
        <span className="text-[11px] text-slate-400">Every part can be put back on its own afterwards.</span>
      </div>
    </div>
  );
}

export const UndoButton = ({ onClick, busy, label = "Put it back", testid }) => (
  <Button data-testid={testid} size="sm" variant="outline" disabled={busy} onClick={onClick}
    className="rounded-lg h-7 text-xs"><Undo2 className="w-3.5 h-3.5 mr-1" />{label}</Button>
);
